// Central icon + brand-colour registry. Skill/tech names used anywhere in
// src/data/profile.js must have an entry here.
//
// Claude, n8n and Cursor have no brand glyph in react-icons v4, so they use a
// representative icon tinted with the product's brand colour rather than a
// hand-drawn imitation of the real logo.
import {
  SiOpenai,
  SiWhatsapp,
  SiGooglesheets,
  SiGooglecalendar,
  SiReact,
  SiNodedotjs,
  SiPostgresql,
  SiHtml5,
  SiPython,
  SiJavascript,
  SiDocker,
  SiGit,
  SiGithub,
  SiLinux,
  SiMeta,
  SiAmazonaws,
  SiGooglecloud,
  SiWordpress,
  SiTelegram,
  SiAndroidstudio,
  SiVercel,
  SiTensorflow,
  SiPytorch,
  SiTableau,
  SiAirtable,
  SiFirebase,
  SiFlask,
  SiPostman,
  SiOpenjdk,
  SiExpress,
  SiCss3,
} from "react-icons/si";
import {
  TbSparkles,
  TbDatabaseSearch,
  TbPlugConnected,
  TbMessageChatbot,
  TbApi,
  TbShieldLock,
  TbDatabase,
  TbTerminal2,
  TbCursorText,
  TbSchool,
  TbMicrophone,
  TbBrain,
  TbBolt,
  TbChartHistogram,
  TbStars,
} from "react-icons/tb";
import { LuWorkflow, LuWebhook } from "react-icons/lu";

const CLAUDE = "#d97757";
const N8N = "#ea4b71";

export const techIcons = {
  // AI / LLM
  "Claude API": { Icon: TbSparkles, color: CLAUDE },
  "Claude Code": { Icon: TbTerminal2, color: CLAUDE },
  "Claude MCP": { Icon: TbPlugConnected, color: CLAUDE },
  "OpenAI API": { Icon: SiOpenai, color: "#412991" },
  "LLM APIs": { Icon: TbSparkles, color: CLAUDE },
  RAG: { Icon: TbDatabaseSearch, color: "#0f9d94" },
  MCP: { Icon: TbPlugConnected, color: CLAUDE },
  "Prompt Engineering": { Icon: TbMessageChatbot, color: "#7c5cff" },
  "LLaMA 2-7B": { Icon: TbBrain, color: "#0866ff" },
  LLaMA: { Icon: TbBrain, color: "#0866ff" },
  Gemini: { Icon: TbStars, color: "#1a73e8" },
  "Generative AI": { Icon: TbSparkles, color: "#a855f7" },
  QLoRA: { Icon: TbBolt, color: "#f7a41d" },
  "Fine-Tuning (QLoRA)": { Icon: TbBolt, color: "#f7a41d" },
  ChromaDB: { Icon: TbDatabase, color: "#ff9c3f" },
  Gradio: { Icon: TbMessageChatbot, color: "#f97316" },
  "Speech Recognition": { Icon: TbMicrophone, color: "#0ea5e9" },

  // Automation
  n8n: { Icon: LuWorkflow, color: N8N },
  "WhatsApp Business API": { Icon: SiWhatsapp, color: "#25d366" },
  Webhooks: { Icon: LuWebhook, color: "#6366f1" },
  "Google Sheets API": { Icon: SiGooglesheets, color: "#0f9d58" },
  "Google Calendar API": { Icon: SiGooglecalendar, color: "#4285f4" },
  Telegram: { Icon: SiTelegram, color: "#26a5e4" },
  "Telegram API": { Icon: SiTelegram, color: "#26a5e4" },
  Airtable: { Icon: SiAirtable, color: "#e0455f" },

  // Full-stack
  React: { Icon: SiReact, color: "#0e97b8" },
  "Node.js": { Icon: SiNodedotjs, color: "#5fa04e" },
  "REST APIs": { Icon: TbApi, color: "#0ea5e9" },
  OAuth: { Icon: TbShieldLock, color: "#2f855a" },
  PostgreSQL: { Icon: SiPostgresql, color: "#4169e1" },
  "HTML/CSS": { Icon: SiHtml5, color: "#e34f26" },
  CSS: { Icon: SiCss3, color: "#1572b6" },
  Express: { Icon: SiExpress, color: "#5b6470" },
  Flask: { Icon: SiFlask, color: "#4b5563" },
  WordPress: { Icon: SiWordpress, color: "#21759b" },
  Moodle: { Icon: TbSchool, color: "#f98012" },
  Vercel: { Icon: SiVercel, color: "#111111" },

  // ML & data
  TensorFlow: { Icon: SiTensorflow, color: "#ff6f00" },
  PyTorch: { Icon: SiPytorch, color: "#ee4c2c" },
  Tableau: { Icon: SiTableau, color: "#1f77b4" },
  "Data Analytics": { Icon: TbChartHistogram, color: "#0891b2" },

  // Languages
  Python: { Icon: SiPython, color: "#3776ab" },
  JavaScript: { Icon: SiJavascript, color: "#c9a227" },
  SQL: { Icon: TbDatabase, color: "#00758f" },
  Java: { Icon: SiOpenjdk, color: "#e76f00" },

  // Tools / DevOps
  Cursor: { Icon: TbCursorText, color: "#111111" },
  Docker: { Icon: SiDocker, color: "#2496ed" },
  Git: { Icon: SiGit, color: "#f05032" },
  GitHub: { Icon: SiGithub, color: "#181717" },
  Linux: { Icon: SiLinux, color: "#b08800" },
  Meta: { Icon: SiMeta, color: "#0866ff" },
  AWS: { Icon: SiAmazonaws, color: "#e08c00" },
  GCP: { Icon: SiGooglecloud, color: "#4285f4" },
  "Android Studio": { Icon: SiAndroidstudio, color: "#3ddc84" },
  Postman: { Icon: SiPostman, color: "#ff6c37" },
  Firebase: { Icon: SiFirebase, color: "#dd9d05" },
};

const fallback = { Icon: TbSparkles, color: "#31afe4" };

export const getTech = (name) => techIcons[name] || fallback;

// Renders just the glyph for `name`. className lands on the <svg> itself so the
// scraper hooks (e.g. skills-card-img) stay attached to a real element.
export const TechIcon = ({ name, className, colored = true }) => {
  const { Icon, color } = getTech(name);
  return <Icon className={className} color={colored ? color : undefined} />;
};

export default techIcons;
